/**
 * ServiceUnavailable.tsx
 *
 * Purpose: Themed "service unavailable" panel with a manual retry action.
 * Created: 2026-09-21 (PRD001A 001A.7a)
 *
 * WHO: `ProtectedRoute` (src/App.tsx) when `AuthContext.authStatus === 'unavailable'`,
 *      and any future caller that needs to distinguish "the backend/auth store is
 *      down" from "you are signed out" or "you don't have access."
 * WHAT: Renders a non-alarming, honest "can't reach the service right now" message
 *      plus a "Try again" button. Never implies the user is signed out or forbidden.
 * WHERE: Mythbound.App/src/components/common/ServiceUnavailable.tsx.
 * WHY: PRD001A 001A.7a requires an explicit unavailable UI — a 503/network failure
 *      must not be silently converted into a login redirect (which would read as
 *      "you're signed out") or an empty/forbidden page. This component is the
 *      shared visual for that state so it isn't reinvented per call site.
 */

import React from 'react';
import { Box, Button, Heading, Text, VStack, Center, Icon } from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';

export interface ServiceUnavailableProps {
  /** Shown as the panel title. Defaults to a generic, honest message. */
  title?: string;
  /** Shown as supporting body text. Defaults to a generic retry hint. */
  description?: string;
  /** Called when the user clicks "Try again." Typically `AuthContext.checkSessionStatus`. */
  onRetry: () => void;
  /** Disables the retry button and shows "Retrying..." while a retry is in flight. */
  isRetrying?: boolean;
}

/**
 * Shared unavailable-state panel.
 *
 * Deliberately does not say "signed out," "forbidden," or "not found" — those
 * are different, more specific states with their own UI. This component is
 * only for "we could not reach the service to find out," which is what an
 * auth-store 503 or a network failure actually means.
 */
export const ServiceUnavailable: React.FC<ServiceUnavailableProps> = ({
  title = 'Service temporarily unavailable',
  description = "We couldn't reach Mythbound right now. This is usually temporary — please try again in a moment.",
  onRetry,
  isRetrying = false,
}) => {
  return (
    <Center minH="60vh" p={6}>
      <VStack spacing={4} maxW="sm" textAlign="center">
        <Icon as={AlertTriangle} boxSize={10} color="orange.400" aria-hidden="true" />
        <Heading size="md">{title}</Heading>
        <Text color="gray.500">{description}</Text>
        <Box pt={2}>
          <Button colorScheme="blue" onClick={onRetry} isLoading={isRetrying} loadingText="Retrying...">
            Try again
          </Button>
        </Box>
      </VStack>
    </Center>
  );
};

export default ServiceUnavailable;
